import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule, NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import * as QRCode from 'qrcode';
import { Subscription, finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { CommonService } from 'src/app/services/common.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ThemeConfig } from 'src/app/shared/models/theme-config';
import { ConstantUtil } from 'src/app/util/constant.util';

type WebchatFeatureConfig = {
  hideInputWhenAiServing: boolean;
  showVoiceButton: boolean;
  showMoreMenu: boolean;
  showAttachmentButton: boolean;
  showCloseButton: boolean;
};

type WebchatServiceTermsConfig = {
  enabled: boolean;
  title: string;
  version: string;
  content: string;
  agreeButtonText: string;
  cancelButtonText: string;
};

type WebchatAboutConfig = {
  title: string;
  versionDescription: string;
};

type WebchatAboutConfigFallbackState = {
  titleUsesDefaultFallback: boolean;
  versionDescriptionUsesDefaultFallback: boolean;
};

type WebchatPostMessageConfig = {
  enabled: boolean;
};

type WebchatFeatureConfigKey = keyof WebchatFeatureConfig;
type WebchatLoginMode = 'anonymous' | 'mobile';
type WebchatFloatWindowConfig = {
  position: 'left-bottom' | 'right-bottom';
  buttonSize: 'small' | 'medium' | 'large';
  badgeColor: string;
  animation: 'none' | 'pulse' | 'bounce' | 'shake';
  bottomOffset: number;
  rightOffset: number;
  leftOffset: number;
  iconUrl: string | null;
};
const DEFAULT_FLOAT_WINDOW_CONFIG: WebchatFloatWindowConfig = {
  position: 'right-bottom',
  buttonSize: 'medium',
  badgeColor: '#1890ff',
  animation: 'none',
  bottomOffset: 20,
  rightOffset: 20,
  leftOffset: 20,
  iconUrl: null
};
const FEATURE_TOGGLE_KEYS: WebchatFeatureConfigKey[] = [
  'hideInputWhenAiServing',
  'showVoiceButton',
  'showMoreMenu',
  'showAttachmentButton',
  'showCloseButton'
];

@Component({
  selector: 'personal-webchat-setting',
  standalone: true,
  imports: [
    CommonModule,
    NzSpinModule,
    NzSwitchModule,
    NzCardModule,
    NzGridModule,
    NzTabsModule,
    NzUploadModule,
    NzButtonModule,
    NzIconModule,
    NzFormModule,
    NzRadioModule,
    NzInputModule,
    NzSelectModule,
    NzToolTipModule,
    NzModalModule,
    NzCheckboxModule,
    NzInputNumberModule,
    NzTagModule,
    FormsModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './personal-webchat-setting.component.html',
  styleUrls: ['./personal-webchat-setting.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalWebchatSettingComponent implements OnInit, OnChanges {
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly msg = inject(NzMessageService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly imageService = inject(ImageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly commonService = inject(CommonService);

  loading = false;
  tenantId: string = '';
  serviceNumberId: string = '';
  @Input() tenantIdInput: string = '';
  @Input() serviceNumberIdInput: string = '';
  @Input() serviceNumberAvatarUrl: string = '';
  webchatUrl: string = '';
  selectedTab = 0;
  position: 'left-bottom' | 'right-bottom' = DEFAULT_FLOAT_WINDOW_CONFIG.position;
  buttonSize: 'small' | 'medium' | 'large' = DEFAULT_FLOAT_WINDOW_CONFIG.buttonSize;
  badgeColor: string = DEFAULT_FLOAT_WINDOW_CONFIG.badgeColor;
  animation: 'none' | 'pulse' | 'bounce' | 'shake' = DEFAULT_FLOAT_WINDOW_CONFIG.animation;
  avatarUrl: string = '';
  // 可选的头部图片文件（用于一次性随保存请求上传）
  headerImageFile: File | null = null;
  private defaultHeaderImageUrl: string = '';
  private runtimeConfigUrl: string = '';
  unreadCount = 3;
  bottomOffset: number = 20;
  rightOffset: number = 20;
  leftOffset: number = 20;
  // 集成代码弹窗
  integrationCodeModalVisible = false;
  integrationCodeSnippet = '';
  shareLinkModalVisible = false;
  shareQrModalVisible = false;
  shareQrCodeDataUrl = '';
  shareQrGenerating = false;
  floatPreviewModalVisible = false;
  floatPreviewSrcdoc: SafeHtml | null = null;
  floatPreviewSrcdocRaw = '';
  shareLoginMode: WebchatLoginMode = 'anonymous';
  saving = false;
  unsavedStyleDirty = false;
  unsavedBubbleVisible = false;
  isCompactViewport = false;
  mobileDesktopPromptDismissed = false;
  featureConfig = this.createDefaultFeatureConfig();
  featureConfigCustomized = false;
  serviceTermsConfig = this.createDefaultServiceTermsConfig();
  aboutConfig = this.createDefaultAboutConfig();
  postMessageConfig = this.createDefaultPostMessageConfig();
  private defaultAvatarHintShown = false;
  private serviceNumberHeaderImageUrl: string = '';
  private loadedFloatWindow: WebchatFloatWindowConfig | null = null;
  private loadedFeatureConfig: WebchatFeatureConfig = this.createDefaultFeatureConfig();
  private loadedFeatureConfigCustomized = false;
  private loadedServiceTermsConfig = this.createDefaultServiceTermsConfig();
  private loadedAboutConfig = this.createDefaultAboutConfig();
  private loadedAboutFallbackState: WebchatAboutConfigFallbackState = {
    titleUsesDefaultFallback: true,
    versionDescriptionUsesDefaultFallback: true
  };
  private loadedPostMessageConfig = this.createDefaultPostMessageConfig();

  // Theme style tab variables (参考服務號主題配置)
  primaryColor: string = '';
  backgroundColor: string = '';
  headerBg: string = '';
  headerTextColor: string = '';
  footerBg: string = '';
  customerBubbleBg: string = '';
  customerBubbleText: string = '';
  agentBubbleBg: string = '';
  agentBubbleText: string = '';
  bubbleRadius: string = '';
  windowRadius: string = '';
  boxShadowMedium: string = '';
  fontSizeBase: string = '';
  shadowPreset: 'none' | 'light' | 'standard' | 'deep' | 'custom' | null = null;
  private loadedThemeCfg: {
    primaryColor: string;
    backgroundColor: string;
    headerBg: string;
    headerTextColor: string;
    footerBg: string;
    customerBubbleBg: string;
    customerBubbleText: string;
    agentBubbleBg: string;
    agentBubbleText: string;
    bubbleRadius: string;
    windowRadius: string;
    boxShadowMedium: string;
    fontSizeBase: string;
  } | null = null;

  // 主题选择
  themeOptions: Array<{ id: string; name: string }> = [];
  selectedThemeId: string | null = null;
  featureItems: Array<{
    key: WebchatFeatureConfigKey;
    icon: string;
    titleKey: string;
    descKey: string;
    section: 'behavior' | 'display';
  }> = [
    {
      key: 'hideInputWhenAiServing',
      icon: 'robot',
      titleKey: 'servicenumber.webchat.feature.hideInput.title',
      descKey: 'servicenumber.webchat.feature.hideInput.desc',
      section: 'behavior'
    },
    {
      key: 'showVoiceButton',
      icon: 'audio',
      titleKey: 'servicenumber.webchat.feature.voiceButton.title',
      descKey: 'servicenumber.webchat.feature.voiceButton.desc',
      section: 'display'
    },
    {
      key: 'showMoreMenu',
      icon: 'appstore',
      titleKey: 'servicenumber.webchat.feature.moreMenu.title',
      descKey: 'servicenumber.webchat.feature.moreMenu.desc',
      section: 'display'
    },
    {
      key: 'showAttachmentButton',
      icon: 'paper-clip',
      titleKey: 'servicenumber.webchat.feature.attachmentButton.title',
      descKey: 'servicenumber.webchat.feature.attachmentButton.desc',
      section: 'display'
    },
    {
      key: 'showCloseButton',
      icon: 'close-circle',
      titleKey: 'servicenumber.webchat.feature.closeButton.title',
      descKey: 'servicenumber.webchat.feature.closeButton.desc',
      section: 'display'
    }
  ];

  // 监控规则配置
  ruleModalVisible = false;
  editingRuleIndex: number | null = null;
  rules: Array<{
    name: string;
    enabled: boolean;
    pageMatchEnabled: boolean;
    pageMatchType: 'url' | 'title';
    pageMatchKeyword: string;
    dwellEnabled: boolean;
    dwellSeconds: number;
    scrollEnabled: boolean;
    scrollPercent: number;
    content: string;
    buttonAnim: 'pulse' | 'bounce' | 'none';
    priority: number;
  }> = [];

  newRule = this.createEmptyRule();

  onShadowPresetChange(preset: 'none' | 'light' | 'standard' | 'deep' | 'custom'): void {
    this.shadowPreset = preset;
    switch (preset) {
      case 'none':
        this.boxShadowMedium = 'none';
        break;
      case 'light':
        this.boxShadowMedium = '0 4px 12px rgba(0,0,0,0.06)';
        break;
      case 'standard':
        this.boxShadowMedium = '0 8px 24px rgba(0,0,0,0.06)';
        break;
      case 'deep':
        this.boxShadowMedium = '0 12px 32px rgba(0,0,0,0.12)';
        break;
      case 'custom':
        // 保留当前自定义值，不做修改
        break;
    }
  }

  get bubbleSize(): number {
    switch (this.buttonSize) {
      case 'small':
        return 50;
      case 'large':
        return 70;
      default:
        return 60;
    }
  }

  get showAboutClearFallbackNotice(): boolean {
    return !String(this.aboutConfig.title || '').trim() || !String(this.aboutConfig.versionDescription || '').trim();
  }

  get showAboutDefaultPreviewNotice(): boolean {
    if (this.showAboutClearFallbackNotice) {
      return false;
    }

    const defaults = this.createDefaultAboutConfig();
    return (
      (this.loadedAboutFallbackState.titleUsesDefaultFallback && this.aboutConfig.title === defaults.title) ||
      (this.loadedAboutFallbackState.versionDescriptionUsesDefaultFallback &&
        this.aboutConfig.versionDescription === defaults.versionDescription)
    );
  }

  setPosition(p: 'left-bottom' | 'right-bottom'): void {
    this.position = p;
    this.onFloatStyleChanged();
    this.cdr.detectChanges();
  }

  setAnimation(a: 'none' | 'pulse' | 'bounce' | 'shake'): void {
    this.animation = a;
    this.onFloatStyleChanged();
  }

  get animationClass(): string {
    return `anim-${this.animation}`;
  }

  get behaviorFeatureItems() {
    return this.featureItems.filter(item => item.section === 'behavior');
  }

  get displayFeatureItems() {
    return this.featureItems.filter(item => item.section === 'display');
  }

  get enabledFeatureCount(): number {
    return (
      FEATURE_TOGGLE_KEYS.filter(key => this.featureConfig[key]).length +
      (this.serviceTermsConfig.enabled ? 1 : 0) +
      (this.postMessageConfig.enabled ? 1 : 0)
    );
  }

  get disabledFeatureCount(): number {
    return FEATURE_TOGGLE_KEYS.length + 2 - this.enabledFeatureCount;
  }

  get isUsingDefaultFeatureConfig(): boolean {
    return !this.featureConfigCustomized;
  }

  get hasUnsavedFeatureConfig(): boolean {
    return this.isFeatureConfigDirty();
  }

  get isEmbeddedInDetail(): boolean {
    return !!this.tenantIdInput || !!this.serviceNumberIdInput;
  }

  get currentWebchatShareUrl(): string {
    return this.buildWebchatShareUrl(this.shareLoginMode);
  }

  get showMobileDesktopPrompt(): boolean {
    return this.isCompactViewport && !this.mobileDesktopPromptDismissed;
  }

  get integrationCodeUnavailableReason(): string | null {
    if (!this.serviceNumberId && !localStorage.getItem('current_tenant_boss_snid')) {
      return this.i18n.fanyi('servicenumber.webchat.integration.unavailable.missingBaseConfig');
    }

    const src = this.loadedFloatWindow;
    const hasSavedFloatWindow = !!(src && src.position && src.buttonSize && this.webchatUrl);
    const hasUnsavedFloatWindow = (src ? this.isFloatWindowDirty(src) : false) || !!this.headerImageFile;

    if (!hasSavedFloatWindow || hasUnsavedFloatWindow) {
      return this.i18n.fanyi('servicenumber.webchat.integration.unavailable.saveFloatWindowFirst');
    }

    return null;
  }

  get canOpenIntegrationCode(): boolean {
    return !this.integrationCodeUnavailableReason;
  }

  handleUpload = (item: NzUploadXHRArgs): Subscription => {
    const nzFile = item.file as any;
    const file: File | undefined = (nzFile?.originFileObj as File) || (nzFile as File);
    if (!file) {
      item.onError?.('empty_file', item.file);
      return new Subscription();
    }
    // 缓存文件以便保存时作为 headerImage 一并上传
    this.headerImageFile = file;
    this.defaultHeaderImageUrl = '';
    const reader = new FileReader();
    reader.onload = () => {
      this.avatarUrl = String(reader.result || this.avatarUrl);
      item.onSuccess?.({}, item.file, null);
      // OnPush 下确保变更检测触发，避免首次上传不显示
      this.cdr.detectChanges();
      this.onFloatStyleChanged();
    };
    reader.onerror = () => item.onError?.('read_error', item.file);
    reader.readAsDataURL(file);
    return new Subscription();
  };

  ngOnInit(): void {
    this.updateViewportState();
    const routeId = this.route.snapshot.paramMap.get('id') || '';
    const queryServiceNumberId = this.route.snapshot.queryParamMap.get('serviceNumberId') || '';
    // 优先使用外部传入 ID，兼容独立页面路由与历史本地缓存
    this.tenantId = this.tenantIdInput || routeId || '';
    const cachedBossServiceNumberId =
      this.commonService.getCurrentTenantBossSnid() || localStorage.getItem('current_tenant_boss_snid') || '';
    this.serviceNumberId = this.serviceNumberIdInput || queryServiceNumberId || cachedBossServiceNumberId || '';
    if (this.serviceNumberId) {
      this.fetchWebchatItem(this.serviceNumberId);
    }
    // 加载主题列表
    this.loadThemeOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['serviceNumberAvatarUrl'] && this.shouldUseServiceNumberAvatarAsDefault()) {
      this.trySetServiceNumberAvatarSync();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportState();
  }

  dismissMobileDesktopPrompt(): void {
    this.mobileDesktopPromptDismissed = true;
    this.cdr.markForCheck();
  }

  // 加载主题列表（仅取 name 与 id 字段）
  loadThemeOptions(): void {
    const params = {
      pageIndex: 0,
      pageSize: 50,
      key: '',
      direction: 'desc',
      refreshTime: Date.now()
    } as any;
    this.http.post(ConstantUtil.BASE_THEME_V1_LIST, params).subscribe((res: any) => {
      const items = (res?.data?.items || []).map((it: any) => ({ id: String(it?.id || ''), name: String(it?.name || '') }));
      this.themeOptions = this.mergeThemeOptions(items);
    });
  }

  // 加载个人WebChat配置详情，并将各字段映射到UI
  private fetchWebchatItem(id: string): void {
    this.saving = false;
    this.loading = true;
    this.defaultHeaderImageUrl = '';
    this.http
      .post(`${ConstantUtil.TENANT_SERVICENUMBER_WEBCHAT_V1_ITEM}/${id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((res: any) => {
        const data = res?.data ?? {};
        // 基本标识
        this.tenantId = data.tenantId || this.tenantId;
        this.serviceNumberId = data.serviceNumberId || this.serviceNumberId;
        this.webchatUrl = String(data.webchatUrl || '').trim();
        this.runtimeConfigUrl = String(data.configUrl || '').trim();
        this.serviceNumberHeaderImageUrl = String(data.serviceNumberHeaderImageUrl || '').trim();
        const sourceThemeId: string | null = data.sourceThemeId ?? data.theme?.id ?? null;
        const sourceThemeName: string | null = data.sourceThemeName ?? data.theme?.name ?? null;
        this.syncSelectedTheme(sourceThemeId, sourceThemeName);

        // 主题配置优先：直接使用 themeConfigJson；若无，则回落到 sourceThemeId 调用系统主题
        try {
          const cfgStr = data.themeConfigJson;
          const cfg = typeof cfgStr === 'string' ? JSON.parse(cfgStr) : cfgStr;
          if (cfg) {
            const n = cfg as ThemeConfig;
            this.primaryColor = n.primaryColor ?? this.primaryColor;
            this.backgroundColor = n.backgroundColor ?? this.backgroundColor;
            this.headerBg = n.headerBg ?? this.headerBg;
            this.headerTextColor = n.headerTextColor ?? this.headerTextColor;
            this.footerBg = n.footerBg ?? this.footerBg;
            this.customerBubbleBg = n.customerBubbleBg ?? this.customerBubbleBg;
            this.customerBubbleText = n.customerBubbleText ?? this.customerBubbleText;
            this.agentBubbleBg = n.agentBubbleBg ?? this.agentBubbleBg;
            this.agentBubbleText = n.agentBubbleText ?? this.agentBubbleText;
            this.bubbleRadius = n.bubbleRadius ?? this.bubbleRadius;
            this.windowRadius = n.windowRadius ?? this.windowRadius;
            const shadowMedium = (n.boxShadow?.medium ?? '').toString();
            if (shadowMedium) this.boxShadowMedium = shadowMedium;
            const fontBase = (n.fontSize?.base ?? '').toString();
            if (fontBase) this.fontSizeBase = fontBase;
            this.updateShadowPresetFromForm();
            this.loadedThemeCfg = {
              primaryColor: this.primaryColor || '',
              backgroundColor: this.backgroundColor || '',
              headerBg: this.headerBg || '',
              headerTextColor: this.headerTextColor || '',
              footerBg: this.footerBg || '',
              customerBubbleBg: this.customerBubbleBg || '',
              customerBubbleText: this.customerBubbleText || '',
              agentBubbleBg: this.agentBubbleBg || '',
              agentBubbleText: this.agentBubbleText || '',
              bubbleRadius: this.bubbleRadius || '',
              windowRadius: this.windowRadius || '',
              boxShadowMedium: this.boxShadowMedium || '',
              fontSizeBase: this.fontSizeBase || ''
            };
          } else if (sourceThemeId) {
            // 仅记录主题ID，不进行回退应用，避免默认值覆盖
            this.selectedThemeId = String(sourceThemeId);
          }
        } catch {
          if (sourceThemeId) {
            // 解析失败同样只记录 ID，不自动应用
            this.selectedThemeId = String(sourceThemeId);
          }
        }

        // 浮窗配置
        try {
          const fwStr = data.floatWindowJson;
          const fw = typeof fwStr === 'string' ? JSON.parse(fwStr) : fwStr;
          const normalizedFloatWindow = this.normalizeFloatWindowConfig(fw);
          const rawAvatarUrl = this.readRawFloatWindowAvatarUrl(fw);
          this.position = normalizedFloatWindow.position;
          this.buttonSize = normalizedFloatWindow.buttonSize;
          this.badgeColor = normalizedFloatWindow.badgeColor;
          this.animation = normalizedFloatWindow.animation;
          this.bottomOffset = normalizedFloatWindow.bottomOffset;
          this.rightOffset = normalizedFloatWindow.rightOffset;
          this.leftOffset = normalizedFloatWindow.leftOffset;
          this.loadedFloatWindow = normalizedFloatWindow;

          if (normalizedFloatWindow.iconUrl) {
            this.avatarUrl = normalizedFloatWindow.iconUrl;
            this.defaultHeaderImageUrl = '';
          } else if (rawAvatarUrl) {
            this.avatarUrl = rawAvatarUrl;
            this.defaultHeaderImageUrl = '';
          } else {
            this.trySetServiceNumberAvatarSync();
          }
        } catch {}

        // 若仍未命中配置头图，尝试使用服务号默认头图
        if (!this.avatarUrl) {
          this.trySetServiceNumberAvatarSync();
        }

        // 监控规则配置
        try {
          const rulesStr = data.monitorRulesJson;
          const parsed = typeof rulesStr === 'string' ? JSON.parse(rulesStr) : rulesStr;
          if (Array.isArray(parsed)) {
            this.rules = parsed.map(r => ({
              name: r.name ?? '',
              enabled: !!r.enabled,
              pageMatchEnabled: true,
              pageMatchType: (r.pageMatchType as 'url' | 'title') ?? 'url',
              pageMatchKeyword: r.pageMatchKeyword ?? '',
              dwellEnabled: !!r.dwellEnabled,
              dwellSeconds: typeof r.dwellSeconds === 'number' ? r.dwellSeconds : 30,
              scrollEnabled: !!r.scrollEnabled,
              scrollPercent: typeof r.scrollPercent === 'number' ? r.scrollPercent : 50,
              content: r.content ?? '',
              buttonAnim: (r.buttonAnim as 'pulse' | 'bounce' | 'none') ?? 'pulse',
              priority: typeof r.priority === 'number' ? r.priority : 0
            }));
            this.sortRulesByPriority();
          }
        } catch {}

        const featureState = this.readFeatureConfig(data.featureConfigJson);
        this.featureConfig = featureState.config;
        this.featureConfigCustomized = featureState.customized;
        this.serviceTermsConfig = featureState.serviceTermsConfig;
        this.aboutConfig = featureState.aboutConfig;
        this.postMessageConfig = featureState.postMessageConfig;
        this.loadedFeatureConfig = this.cloneFeatureConfig(featureState.config);
        this.loadedFeatureConfigCustomized = featureState.customized;
        this.loadedServiceTermsConfig = this.cloneServiceTermsConfig(featureState.serviceTermsConfig);
        this.loadedAboutConfig = this.cloneAboutConfig(featureState.aboutConfig);
        this.loadedAboutFallbackState = featureState.aboutFallbackState;
        this.loadedPostMessageConfig = this.clonePostMessageConfig(featureState.postMessageConfig);

        // 显式刷新预览
        this.cdr.detectChanges();
      });
  }

  // 从缓存中获取当前租户信息
  private getCurrentTenantFromCache(): any | null {
    const tenantStr = localStorage.getItem('current_tenant');
    if (!tenantStr) return null;
    try {
      const t = JSON.parse(tenantStr);
      return t;
    } catch {
      return null;
    }
  }

  // 读取本地缓存的头像URL（HeaderTenantComponent 写入的结构）
  private getAvatarFromLocalCache(avatarId: string): string | null {
    const cacheKey = `tenant_avatar_${avatarId}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    try {
      const obj = JSON.parse(cached);
      const url = obj?.url ?? '';
      const timestamp = Number(obj?.timestamp ?? 0);
      const CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000;
      if (!url) return null;
      if (timestamp && Date.now() - timestamp > CACHE_EXPIRY) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      return url;
    } catch {
      localStorage.removeItem(cacheKey);
      return null;
    }
  }

  private getServiceNumberAvatarUrl(): string {
    return this.serviceNumberHeaderImageUrl || (this.serviceNumberAvatarUrl || '').trim();
  }

  private shouldUseServiceNumberAvatarAsDefault(): boolean {
    if (!this.getServiceNumberAvatarUrl() || this.loadedFloatWindow?.iconUrl || this.headerImageFile) {
      return false;
    }
    if (!this.avatarUrl) {
      return true;
    }
    return !!this.defaultHeaderImageUrl && this.avatarUrl === this.defaultHeaderImageUrl;
  }

  // 嵌入服务号资料室时优先使用当前服务号头图作为默认头像
  private trySetServiceNumberAvatarSync(): boolean {
    if (!this.shouldUseServiceNumberAvatarAsDefault()) {
      return false;
    }
    this.avatarUrl = this.getServiceNumberAvatarUrl();
    this.defaultHeaderImageUrl = this.avatarUrl;
    this.cdr.detectChanges();
    return true;
  }

  // 同步方式尝试设置默认头像（保留旧逻辑作为回退）
  private trySetDefaultAvatarSync(): boolean {
    const tenant = this.getCurrentTenantFromCache();
    if (!tenant) return false;
    // 如有路由租户ID，进行匹配
    if (this.tenantId && tenant.id && String(tenant.id) !== String(this.tenantId)) {
      return false;
    }
    if (tenant.logoUrl) {
      this.avatarUrl = String(tenant.logoUrl);
      this.defaultHeaderImageUrl = this.avatarUrl;
      this.cdr.detectChanges();
      if (!this.defaultAvatarHintShown) {
        this.defaultAvatarHintShown = true;
        this.msg.info(this.i18n.fanyi('servicenumber.webchat.useTenantHeaderAsDefaultAvatar'));
      }
      return true;
    }
    if (tenant.avatarId) {
      const cachedUrl = this.getAvatarFromLocalCache(String(tenant.avatarId));
      if (cachedUrl) {
        this.avatarUrl = cachedUrl;
        this.defaultHeaderImageUrl = cachedUrl;
        this.cdr.detectChanges();
        if (!this.defaultAvatarHintShown) {
          this.defaultAvatarHintShown = true;
          this.msg.info(this.i18n.fanyi('servicenumber.webchat.useTenantAvatarAsDefaultAvatar'));
        }
        return true;
      }
    }
    return false;
  }

  private syncSelectedTheme(sourceThemeId: string | null, sourceThemeName: string | null): void {
    if (!sourceThemeId) {
      return;
    }
    const id = String(sourceThemeId);
    this.selectedThemeId = id;
    const name = String(sourceThemeName || '').trim();
    if (name && !this.themeOptions.some(option => option.id === id)) {
      this.themeOptions = [...this.themeOptions, { id, name }];
    }
  }

  private mergeThemeOptions(items: Array<{ id: string; name: string }>): Array<{ id: string; name: string }> {
    const options = items.filter(item => item.id);
    const selectedTheme = this.themeOptions.find(option => option.id === this.selectedThemeId);
    if (selectedTheme && !options.some(option => option.id === selectedTheme.id)) {
      return [...options, selectedTheme];
    }
    return options;
  }

  // 异步加载租户头像（当本地缓存无结果时）
  private tryLoadTenantAvatarAsync(): void {
    const tenant = this.getCurrentTenantFromCache();
    if (!tenant) return;
    if (this.tenantId && tenant.id && String(tenant.id) !== String(this.tenantId)) {
      return;
    }
    if (tenant.avatarId) {
      this.imageService.loadImage(String(tenant.avatarId), 'M').subscribe(url => {
        if (url) {
          if (this.shouldUseServiceNumberAvatarAsDefault()) {
            this.trySetServiceNumberAvatarSync();
            return;
          }
          this.avatarUrl = url;
          this.defaultHeaderImageUrl = url;
          this.cdr.detectChanges();
          if (!this.defaultAvatarHintShown) {
            this.defaultAvatarHintShown = true;
            this.msg.info(this.i18n.fanyi('servicenumber.webchat.useTenantAvatarAsDefaultAvatar'));
          }
        }
      });
    }
  }

  // 选择主题（保留 ID，后续可用于应用配置）
  onThemeSelected(id: string): void {
    this.selectedThemeId = id || null;
    if (this.selectedThemeId) {
      this.applyThemeFromId(this.selectedThemeId);
    }
  }

  // 监控规则：操作
  openAddRule(): void {
    this.editingRuleIndex = null;
    this.newRule = this.createEmptyRule();
    this.ruleModalVisible = true;
  }

  openEditRule(idx: number): void {
    this.editingRuleIndex = idx;
    const r = this.rules[idx];
    this.newRule = { ...r, pageMatchEnabled: true };
    this.ruleModalVisible = true;
  }

  deleteRule(idx: number): void {
    this.rules.splice(idx, 1);
    // 删除规则后立即保存
    this.onSave();
  }

  saveRule(): void {
    this.newRule.pageMatchEnabled = true;
    const validationErrorKey = this.getRuleValidationErrorKey();
    if (validationErrorKey) {
      this.msg.error(this.i18n.fanyi(validationErrorKey));
      return;
    }
    const payload = { ...this.newRule, pageMatchEnabled: true };
    if (this.editingRuleIndex === null || this.editingRuleIndex < 0) {
      this.rules.push(payload);
    } else {
      this.rules[this.editingRuleIndex] = payload;
    }
    this.sortRulesByPriority();
    this.closeRuleModal();
    // 新增或编辑规则后立即保存
    this.onSave();
  }

  closeRuleModal(): void {
    this.ruleModalVisible = false;
  }

  toggleRule(idx: number, enabled: boolean): void {
    this.rules[idx].enabled = enabled;
    // 切换启用状态后也保存，保持后端同步
    this.onSave();
  }

  // TS 注入样式已移除，改用全局样式对 rule-modal 范围内覆盖

  private createEmptyRule() {
    return {
      name: '',
      enabled: true,
      pageMatchEnabled: true,
      pageMatchType: 'url' as 'url' | 'title',
      pageMatchKeyword: '',
      dwellEnabled: true,
      dwellSeconds: 30,
      scrollEnabled: false,
      scrollPercent: 50,
      content: '',
      buttonAnim: 'pulse' as 'pulse' | 'bounce' | 'none',
      priority: 50
    };
  }
  isRuleValid(): boolean {
    return !this.getRuleValidationErrorKey();
  }

  private getRuleValidationErrorKey(): string | null {
    const r = this.newRule;
    if (!r) return 'servicenumber.webchat.rule.requiredFields';
    if (!r.name || String(r.name).trim() === '') return 'servicenumber.webchat.rule.validation.name';
    if (!r.content || String(r.content).trim() === '') return 'servicenumber.webchat.rule.validation.content';
    if (!r.buttonAnim) return 'servicenumber.webchat.rule.validation.buttonAnim';
    if (!r.pageMatchEnabled) return 'servicenumber.webchat.rule.validation.pageMatchRequired';
    if (!r.pageMatchType) return 'servicenumber.webchat.rule.validation.pageMatchType';
    if (!r.pageMatchKeyword || String(r.pageMatchKeyword).trim() === '') return 'servicenumber.webchat.rule.validation.pageMatchKeyword';
    const hasTrigger = !!r.dwellEnabled || !!r.scrollEnabled;
    if (!hasTrigger) return 'servicenumber.webchat.rule.validation.triggerRequired';
    if (r.dwellEnabled) {
      const v = Number(r.dwellSeconds);
      if (!Number.isFinite(v) || v < 1) return 'servicenumber.webchat.rule.validation.dwellSeconds';
    }
    if (r.scrollEnabled) {
      const v = Number(r.scrollPercent);
      if (!Number.isFinite(v) || v < 1 || v > 100) return 'servicenumber.webchat.rule.validation.scrollPercent';
    }
    const p = Number(r.priority);
    if (!Number.isFinite(p)) return 'servicenumber.webchat.rule.validation.priority';
    return null;
  }
  private sortRulesByPriority(): void {
    this.rules = [...this.rules].sort((a, b) => Number(b.priority ?? 0) - Number(a.priority ?? 0));
  }

  // 将主题ID对应的配置覆盖到当前字段，并驱动右侧预览更新
  private applyThemeFromId(id: string): void {
    this.http.post(`${ConstantUtil.BASE_THEME_V1_ITEM}/${id}`, {}).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (!data) return;
        try {
          const cfgStr = data.configJson || data.config || data.themeJson;
          const cfg = typeof cfgStr === 'string' ? JSON.parse(cfgStr) : cfgStr;
          if (cfg) {
            const n = cfg as ThemeConfig;
            // 基础颜色
            this.primaryColor = n.primaryColor ?? this.primaryColor;
            this.backgroundColor = n.backgroundColor ?? this.backgroundColor;
            // 头部/底部
            this.headerBg = n.headerBg ?? this.headerBg;
            this.headerTextColor = n.headerTextColor ?? this.headerTextColor;
            this.footerBg = n.footerBg ?? this.footerBg;
            // 气泡颜色
            this.customerBubbleBg = n.customerBubbleBg ?? this.customerBubbleBg;
            this.customerBubbleText = n.customerBubbleText ?? this.customerBubbleText;
            this.agentBubbleBg = n.agentBubbleBg ?? this.agentBubbleBg;
            this.agentBubbleText = n.agentBubbleText ?? this.agentBubbleText;
            // 圆角与阴影
            this.bubbleRadius = n.bubbleRadius ?? this.bubbleRadius;
            this.windowRadius = n.windowRadius ?? this.windowRadius;
            const shadowMedium = (n.boxShadow?.medium ?? '').toString();
            if (shadowMedium) this.boxShadowMedium = shadowMedium;
            // 字体大小
            const fontBase = (n.fontSize?.base ?? '').toString();
            if (fontBase) this.fontSizeBase = fontBase;
            // 根据阴影值推断预设
            this.updateShadowPresetFromForm();
            this.cdr.detectChanges();
          }
        } catch {
          // ignore parse error
        }
      }
    });
  }

  // 根据当前 boxShadowMedium 推断影子预设值，便于 UI 同步
  private updateShadowPresetFromForm(): void {
    const presetValues: Record<'none' | 'light' | 'standard' | 'deep', string> = {
      none: 'none',
      light: '0 4px 12px rgba(0,0,0,0.06)',
      standard: '0 8px 24px rgba(0,0,0,0.06)',
      deep: '0 12px 32px rgba(0,0,0,0.12)'
    };
    const val = (this.boxShadowMedium || '').trim();
    const match = (Object.keys(presetValues) as Array<'none' | 'light' | 'standard' | 'deep'>).find(k => presetValues[k] === val);
    if (match) {
      this.shadowPreset = match;
    } else if (val === 'none') {
      this.shadowPreset = 'none';
    } else {
      this.shadowPreset = 'custom';
    }
  }

  private updateViewportState(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const nextState = window.innerWidth <= 767;
    if (nextState === this.isCompactViewport) {
      return;
    }
    this.isCompactViewport = nextState;
    if (!nextState) {
      this.mobileDesktopPromptDismissed = false;
    }
    this.cdr.markForCheck();
  }

  toggleFeatureConfig(key: WebchatFeatureConfigKey, enabled: boolean): void {
    this.featureConfig = {
      ...this.featureConfig,
      [key]: enabled
    };
    this.featureConfigCustomized = this.computeFeatureConfigCustomized();
    this.cdr.detectChanges();
  }

  onServiceTermsConfigChange<K extends keyof WebchatServiceTermsConfig>(key: K, value: WebchatServiceTermsConfig[K]): void {
    this.serviceTermsConfig = {
      ...this.serviceTermsConfig,
      [key]: value
    };
    this.featureConfigCustomized = this.computeFeatureConfigCustomized();
    this.cdr.detectChanges();
  }

  onAboutConfigChange<K extends keyof WebchatAboutConfig>(key: K, value: WebchatAboutConfig[K]): void {
    this.aboutConfig = {
      ...this.aboutConfig,
      [key]: value
    };
    this.featureConfigCustomized = this.computeFeatureConfigCustomized();
    this.cdr.detectChanges();
  }

  onPostMessageConfigChange(enabled: boolean): void {
    this.postMessageConfig = {
      enabled
    };
    this.featureConfigCustomized = this.computeFeatureConfigCustomized();
    this.cdr.detectChanges();
  }

  hasServiceTermsCancelButton(): boolean {
    return String(this.serviceTermsConfig.cancelButtonText || '').trim().length > 0;
  }

  useDefaultFeatureConfig(): void {
    this.featureConfig = this.createDefaultFeatureConfig();
    this.serviceTermsConfig = this.createDefaultServiceTermsConfig();
    this.aboutConfig = this.createDefaultAboutConfig();
    this.postMessageConfig = this.createDefaultPostMessageConfig();
    this.featureConfigCustomized = false;
    this.cdr.detectChanges();
  }

  // 保存当前设置到后端
  onSave(): void {
    // 保存前校验：浮动窗口必须有头图
    if (!this.avatarUrl && !this.headerImageFile) {
      const applied = this.trySetServiceNumberAvatarSync();
      if (!applied) {
        this.msg.error(this.i18n.fanyi('servicenumber.webchat.floatWindow.avatarRequired'));
        return;
      }
    }
    const themeCfg: ThemeConfig = {
      primaryColor: this.primaryColor,
      backgroundColor: this.backgroundColor,
      headerBg: this.headerBg,
      headerTextColor: this.headerTextColor,
      footerBg: this.footerBg,
      customerBubbleBg: this.customerBubbleBg,
      customerBubbleText: this.customerBubbleText,
      agentBubbleBg: this.agentBubbleBg,
      agentBubbleText: this.agentBubbleText,
      bubbleRadius: this.bubbleRadius,
      windowRadius: this.windowRadius,
      boxShadow: { medium: this.boxShadowMedium },
      fontSize: { base: this.fontSizeBase }
    };

    const normalizedFloatWindow = this.normalizeFloatWindowConfig({
      position: this.position,
      buttonSize: this.buttonSize,
      badgeColor: this.badgeColor,
      animation: this.animation,
      bottomOffset: this.bottomOffset,
      rightOffset: this.rightOffset,
      leftOffset: this.leftOffset,
      avatarUrl: this.avatarUrl
    });

    const floatWindow: any = {
      position: normalizedFloatWindow.position,
      buttonSize: normalizedFloatWindow.buttonSize,
      badgeColor: normalizedFloatWindow.badgeColor,
      animation: normalizedFloatWindow.animation,
      bottomOffset: normalizedFloatWindow.bottomOffset,
      rightOffset: normalizedFloatWindow.rightOffset,
      leftOffset: normalizedFloatWindow.leftOffset
    };

    // 当未上传新头图文件时，将现有的图标URL写回保存的 floatWindowJson，避免后端清空
    const existingIcon = (this.loadedFloatWindow?.iconUrl || '').trim();
    const existingIconIsHttp = existingIcon.startsWith('http://') || existingIcon.startsWith('https://');
    // 若存在有效的 http/https 图标且当前没有新文件上传，则写入 headerImage 与兼容的 avatarUrl
    if (!this.headerImageFile && existingIconIsHttp) {
      floatWindow.headerImage = { url: existingIcon };
      floatWindow.avatarUrl = existingIcon;
    }

    // 按照接口要求使用 multipart/form-data 保存
    const fields: Record<string, string> = {
      serviceNumberId: this.serviceNumberId || localStorage.getItem('current_tenant_boss_snid') || '',
      themeConfigJson: JSON.stringify(themeCfg),
      floatWindowJson: JSON.stringify(floatWindow),
      monitorRulesJson: JSON.stringify(this.rules),
      featureConfigJson: this.featureConfigCustomized ? JSON.stringify(this.buildFeatureConfigPayload()) : ''
    };
    if (this.selectedThemeId) {
      fields['sourceThemeId'] = this.selectedThemeId;
    }

    const defaultHeaderImageUrl = this.getDefaultHeaderImageUrlForSave();
    if (!this.headerImageFile && defaultHeaderImageUrl) {
      this.saving = true;
      this.loading = true;
      this.createFileFromImageUrl(defaultHeaderImageUrl).then(file => {
        if (!file) {
          this.saving = false;
          this.loading = false;
          this.msg.error(this.i18n.fanyi('servicenumber.webchat.floatWindow.avatarRequired'));
          this.cdr.detectChanges();
          return;
        }
        this.submitWebchatSettings(fields, file);
      });
      return;
    }

    this.submitWebchatSettings(fields, this.headerImageFile);
  }

  private submitWebchatSettings(fields: Record<string, string>, file: File | null): void {
    this.saving = true;
    this.loading = true;
    this.http
      .postFormData(ConstantUtil.TENANT_SERVICENUMBER_WEBCHAT_V1_SAVE, fields, file, 'headerImage')
      .pipe(
        finalize(() => {
          this.loading = false;
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.msg.success(this.i18n.fanyi('servicenumber.webchat.saveSuccess'));
            this.headerImageFile = null;
            this.unsavedStyleDirty = false;
            this.unsavedBubbleVisible = false;
            const id = this.serviceNumberId || localStorage.getItem('current_tenant_boss_snid') || '';
            if (id) {
              this.fetchWebchatItem(id);
            }
          }
        },
        error: () => {
          this.msg.error(this.i18n.fanyi('servicenumber.webchat.saveFailed'));
        }
      });
  }

  private getDefaultHeaderImageUrlForSave(): string {
    const avatarUrl = (this.avatarUrl || '').trim();
    if (this.loadedFloatWindow?.iconUrl) {
      return '';
    }
    return this.isInlineImageUrl(avatarUrl) ? avatarUrl : '';
  }

  private isHttpUrl(url: string): boolean {
    return url.startsWith('http://') || url.startsWith('https://');
  }

  private isFetchableImageUrl(url: string): boolean {
    return this.isHttpUrl(url) || this.isInlineImageUrl(url);
  }

  private isInlineImageUrl(url: string): boolean {
    return url.startsWith('data:image/') || url.startsWith('blob:');
  }

  private async createFileFromImageUrl(url: string): Promise<File | null> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        return null;
      }
      const type = blob.type || 'image/png';
      return new File([blob], `header-image.${this.getImageExtension(type, url)}`, { type });
    } catch {
      return null;
    }
  }

  private getImageExtension(type: string, url: string): string {
    const normalizedType = type.toLowerCase();
    if (normalizedType.includes('jpeg') || normalizedType.includes('jpg')) return 'jpg';
    if (normalizedType.includes('png')) return 'png';
    if (normalizedType.includes('gif')) return 'gif';
    if (normalizedType.includes('webp')) return 'webp';
    const path = url.split('?')[0] || '';
    const match = path.match(/\.([a-z0-9]+)$/i);
    return match?.[1] || 'png';
  }

  // 打开“获取集成代码”弹窗，生成示例代码片段
  openIntegrationCode(): void {
    if (!this.canOpenIntegrationCode) {
      this.msg.warning(
        this.integrationCodeUnavailableReason || this.i18n.fanyi('servicenumber.webchat.integration.unavailable.saveFloatWindowFirst')
      );
      return;
    }

    const snId = this.serviceNumberId || localStorage.getItem('current_tenant_boss_snid') || '';
    const tenant = this.getCurrentTenantFromCache();
    const tenantCode = String(tenant?.code ?? '');
    const hostUrl = this.webchatUrl || '';
    const adminOrigin = window.location.origin.replace(/\/$/, '');
    const loaderUrl = `${adminOrigin}/assets/webchat-sdk/aile-webchat-loader.js?v=${Date.now()}`;
    const src = this.loadedFloatWindow;
    const posSrc = (src?.position as any) || this.position || 'right-bottom';
    const pos = posSrc === 'left-bottom' ? 'bottom-left' : 'bottom-right';
    const bottom = Number.isFinite(Number(src?.bottomOffset))
      ? Number(src?.bottomOffset)
      : Number.isFinite(Number(this.bottomOffset))
        ? Number(this.bottomOffset)
        : 20;
    const left = Number.isFinite(Number(src?.leftOffset))
      ? Number(src?.leftOffset)
      : Number.isFinite(Number(this.leftOffset))
        ? Number(this.leftOffset)
        : 20;
    const right = Number.isFinite(Number(src?.rightOffset))
      ? Number(src?.rightOffset)
      : Number.isFinite(Number(this.rightOffset))
        ? Number(this.rightOffset)
        : 20;
    const iconCandidate = (
      (src?.iconUrl || '').trim() ||
      (this.serviceNumberHeaderImageUrl || '').trim() ||
      (this.defaultHeaderImageUrl || '').trim() ||
      (this.avatarUrl || '').trim()
    ).trim();
    const iconUrlRaw = iconCandidate.replace(/'/g, "\\'");
    const iconLine =
      iconUrlRaw && (iconUrlRaw.startsWith('http://') || iconUrlRaw.startsWith('https://')) ? `\n    icon: '${iconUrlRaw}',` : '';
    const apiBase = (this.http.baseUrl || adminOrigin).replace(/\/$/, '');
    const runtimeConfigUrl =
      this.runtimeConfigUrl || `${apiBase}${ConstantUtil.TENANT_SERVICENUMBER_WEBCHAT_V1_PUBLIC_CONFIG}/${encodeURIComponent(snId)}`;
    const monitorConfigUrl = `${apiBase}/tenant/servicenumber/webchat/v1/monitor-rules/${encodeURIComponent(snId)}`;
    const monitorRequestMethod = 'POST';
    const btnSize = src?.buttonSize || this.buttonSize || DEFAULT_FLOAT_WINDOW_CONFIG.buttonSize;
    const anim = src?.animation || this.animation || DEFAULT_FLOAT_WINDOW_CONFIG.animation;
    const badge = (src?.badgeColor || this.badgeColor || DEFAULT_FLOAT_WINDOW_CONFIG.badgeColor) as string;
    const comment = this.i18n.fanyi('servicenumber.webchat.integration.snippetComment');
    const offsetLine = pos === 'bottom-left' ? `\n    leftOffset: ${left},` : `\n    rightOffset: ${right},`;
    this.integrationCodeSnippet = `<!-- ${comment} -->
<script>
(function(w,d,s,o,js,fjs){
  w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments);};
  w[o].l=1*new Date();
  w.AileWebChatConfig = {
    hostUrl: '${hostUrl}',
    id: '${snId}',
    configUrl: '${runtimeConfigUrl}',
    tenantCode: '${tenantCode}',
    action: 'service',
    position: '${pos}',
    bottomOffset: ${bottom},${offsetLine}
    ${iconLine}
    monitorConfigUrl: '${monitorConfigUrl}',
    monitorRequestMethod: '${monitorRequestMethod}',
    buttonSize: '${btnSize}',
    loginMode: 'anonymous',
    animation: '${anim}',
    badgeColor: '${badge}',
    onLoad: function (res) { console.log('onLoad', res); },
    onError: function (err) { console.error('onError', err); }
  };
  js=d.createElement(s);
  js.id=o;
  js.src='${loaderUrl}';
  js.async=1;
  fjs=d.getElementsByTagName(s)[0];
  fjs.parentNode.insertBefore(js,fjs);
})(window,document,'script','AileWebChat');
</script>`;
    this.integrationCodeModalVisible = true;
    this.cdr.detectChanges();
  }

  onShareLoginModeChange(mode: WebchatLoginMode): void {
    this.shareLoginMode = mode || 'anonymous';
    if (this.shareQrModalVisible) {
      this.updateShareQrCode();
    }
    this.cdr.detectChanges();
  }

  buildWebchatShareUrl(loginMode: WebchatLoginMode = 'anonymous'): string {
    const baseUrl = String(this.webchatUrl || '').trim();
    if (!baseUrl) return '';
    const isAbsolute = /^https?:\/\//i.test(baseUrl);
    try {
      const parsed = new URL(baseUrl, window.location.origin);
      const params = parsed.searchParams;
      const snId = String(this.serviceNumberId || '').trim();
      const tenantCode = String(this.commonService.getCurrentTenant()?.code || '').trim();

      if (snId && !params.has('id')) {
        params.set('id', snId);
      }
      if (tenantCode && !params.has('tenantCode')) {
        params.set('tenantCode', tenantCode);
      }
      if (!params.has('action')) {
        params.set('action', 'service');
      }
      if (!params.has('customData')) {
        params.set('customData', 'default');
      }
      params.set('loginMode', loginMode);

      if (isAbsolute) {
        return parsed.toString();
      }
      const result = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      if (baseUrl.startsWith('/')) {
        return result;
      }
      return result.startsWith('/') ? result.slice(1) : result;
    } catch {
      const [path, originalQuery = ''] = baseUrl.split('?');
      const query = new URLSearchParams(originalQuery);
      const snId = String(this.serviceNumberId || '').trim();
      const tenantCode = String(this.commonService.getCurrentTenant()?.code || '').trim();
      if (snId && !query.has('id')) query.set('id', snId);
      if (tenantCode && !query.has('tenantCode')) query.set('tenantCode', tenantCode);
      if (!query.has('action')) query.set('action', 'service');
      if (!query.has('customData')) query.set('customData', 'default');
      query.set('loginMode', loginMode);
      const suffix = query.toString();
      return suffix ? `${path}?${suffix}` : path;
    }
  }

  copyWebchatShareUrl(): void {
    const text = this.currentWebchatShareUrl;
    if (!text) {
      this.msg.warning(this.i18n.fanyi('servicenumber.webchat.share.noUrl'));
      return;
    }
    const nav: any = navigator as any;
    if (nav.clipboard && nav.clipboard.writeText) {
      nav.clipboard
        .writeText(text)
        .then(() => this.msg.success(this.i18n.fanyi('servicenumber.webchat.share.copySuccess')))
        .catch(() => this.copyShareUrlByTextarea(text));
      return;
    }
    this.copyShareUrlByTextarea(text);
  }

  openShareLinkModal(): void {
    if (!this.currentWebchatShareUrl) {
      this.msg.warning(this.i18n.fanyi('servicenumber.webchat.share.noUrl'));
      return;
    }
    this.shareLinkModalVisible = true;
    this.cdr.detectChanges();
  }

  openShareQrModal(): void {
    if (!this.currentWebchatShareUrl) {
      this.msg.warning(this.i18n.fanyi('servicenumber.webchat.share.noUrl'));
      return;
    }
    this.shareQrModalVisible = true;
    this.updateShareQrCode();
    this.cdr.detectChanges();
  }

  openFloatPreviewModal(): void {
    this.refreshFloatPreviewModal();
    this.floatPreviewModalVisible = true;
    this.cdr.detectChanges();
  }

  closeFloatPreviewModal(): void {
    this.floatPreviewModalVisible = false;
    this.cdr.detectChanges();
  }

  closeShareQrModal(): void {
    this.shareQrModalVisible = false;
    this.shareQrCodeDataUrl = '';
    this.shareQrGenerating = false;
  }

  refreshFloatPreviewModal(): void {
    this.floatPreviewSrcdocRaw = this.buildEmbeddedFloatPreviewSrcdoc();
    this.floatPreviewSrcdoc = this.sanitizer.bypassSecurityTrustHtml(this.floatPreviewSrcdocRaw);
    this.cdr.detectChanges();
  }

  downloadShareQr(): void {
    const link = this.currentWebchatShareUrl;
    if (!link) return;
    QRCode.toDataURL(link, { width: 256, margin: 1 })
      .then((url: string) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = `webchat_${this.serviceNumberId || 'link'}_256.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .catch(() => this.msg.error(this.i18n.fanyi('servicenumber.webchat.share.qrDownloadFailed')));
  }

  onFloatStyleChanged(): void {
    const src = this.loadedFloatWindow;
    const dirty = (src ? this.isFloatWindowDirty(src) : true) || !!this.headerImageFile;
    this.unsavedStyleDirty = !!dirty;
    this.unsavedBubbleVisible = !!dirty;
    this.cdr.detectChanges();
  }

  private isFloatWindowDirty(src: WebchatFloatWindowConfig): boolean {
    const posDirty = (this.position ?? null) !== (src.position ?? null);
    const sizeDirty = (this.buttonSize ?? null) !== (src.buttonSize ?? null);
    const badgeDirty = String(this.badgeColor ?? '') !== String(src.badgeColor ?? '');
    const animDirty = (this.animation ?? null) !== (src.animation ?? null);
    const bottomDirty = Number(this.bottomOffset ?? 0) !== Number(src.bottomOffset ?? 0);
    const rightDirty = Number(this.rightOffset ?? 0) !== Number(src.rightOffset ?? 0);
    const leftDirty = Number(this.leftOffset ?? 0) !== Number(src.leftOffset ?? 0);
    return posDirty || sizeDirty || badgeDirty || animDirty || bottomDirty || rightDirty || leftDirty;
  }

  private escapeHtml(value: unknown): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private buildEmbeddedFloatPreviewSrcdoc(): string {
    const snId = String(this.serviceNumberId || localStorage.getItem('current_tenant_boss_snid') || '').trim();
    const tenant = this.getCurrentTenantFromCache();
    const tenantCode = String(tenant?.code ?? '').trim();
    const hostUrl = String(this.webchatUrl || '').trim();
    const adminOrigin = window.location.origin.replace(/\/$/, '');
    const loaderUrl = `${adminOrigin}/assets/webchat-sdk/aile-webchat-loader.js?v=${Date.now()}`;
    const apiBase = (this.http.baseUrl || adminOrigin).replace(/\/$/, '');
    const runtimeConfigUrl =
      this.runtimeConfigUrl || `${apiBase}${ConstantUtil.TENANT_SERVICENUMBER_WEBCHAT_V1_PUBLIC_CONFIG}/${encodeURIComponent(snId)}`;
    const monitorConfigUrl = `${apiBase}/tenant/servicenumber/webchat/v1/monitor-rules/${encodeURIComponent(snId)}`;
    const position = this.position === 'left-bottom' ? 'bottom-left' : 'bottom-right';
    const bottomOffset = Number.isFinite(Number(this.bottomOffset)) ? Number(this.bottomOffset) : 20;
    const leftOffset = Number.isFinite(Number(this.leftOffset)) ? Number(this.leftOffset) : 20;
    const rightOffset = Number.isFinite(Number(this.rightOffset)) ? Number(this.rightOffset) : 20;
    const iconCandidate = (
      (this.loadedFloatWindow?.iconUrl || '').trim() ||
      (this.serviceNumberHeaderImageUrl || '').trim() ||
      (this.defaultHeaderImageUrl || '').trim() ||
      (this.avatarUrl || '').trim()
    );
    const badgeColor = String(this.badgeColor || DEFAULT_FLOAT_WINDOW_CONFIG.badgeColor);
    const animation = String(this.animation || DEFAULT_FLOAT_WINDOW_CONFIG.animation);
    const buttonSize = String(this.buttonSize || DEFAULT_FLOAT_WINDOW_CONFIG.buttonSize);
    const previewTitle = this.i18n.fanyi('servicenumber.webchat.previewWorkspace.documentTitle');
    const chipServiceNumber = this.i18n.fanyi('servicenumber.webchat.previewWorkspace.meta.serviceNumber');
    const chipPosition = this.i18n.fanyi('servicenumber.webchat.previewWorkspace.meta.position');
    const chipButton = this.i18n.fanyi('servicenumber.webchat.previewWorkspace.meta.button');
    const chipAnimation = this.i18n.fanyi('servicenumber.webchat.previewWorkspace.meta.animation');
    const siteNote = this.i18n.fanyi('servicenumber.webchat.previewWorkspace.siteNote');

    const configData: Record<string, unknown> = {
      hostUrl,
      id: snId,
      configUrl: runtimeConfigUrl,
      tenantCode,
      action: 'service',
      position,
      bottomOffset,
      ...(position === 'bottom-left' ? { leftOffset } : { rightOffset }),
      ...(iconCandidate && (iconCandidate.startsWith('http://') || iconCandidate.startsWith('https://')) ? { icon: iconCandidate } : {}),
      monitorConfigUrl,
      monitorRequestMethod: 'POST',
      buttonSize,
      loginMode: 'anonymous',
      animation,
      badgeColor
    };
    const safeConfigJson = JSON.stringify(configData).replace(/</g, '\\u003c');

    return `<!doctype html>
<html lang="zh-CN">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${this.escapeHtml(previewTitle)}</title>
    <style>
      * { box-sizing: border-box; }
      html, body { height: 100%; margin: 0; }
      body {
        font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background:
          radial-gradient(circle at top right, rgba(59, 130, 246, 0.18), transparent 26%),
          linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
        color: #1f2937;
      }
      .shell {
        min-height: 100%;
        padding: 24px;
        display: grid;
        grid-template-rows: auto auto 1fr;
        gap: 16px;
      }
      .hero, .panel {
        background: rgba(255,255,255,0.9);
        border: 1px solid rgba(191, 219, 254, 0.9);
        border-radius: 18px;
        box-shadow: 0 18px 45px rgba(37, 99, 235, 0.08);
      }
      .hero {
        padding: 20px 22px;
      }
      .meta {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
      }
      .chip {
        padding: 8px 12px;
        border-radius: 999px;
        background: #eff6ff;
        color: #1d4ed8;
        font-size: 12px;
        font-weight: 600;
      }
      .panel {
        position: relative;
        overflow: hidden;
        min-height: 360px;
        border-radius: 24px;
        background: linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
        border: 1px solid rgba(191, 219, 254, 0.9);
      }
      .site {
        padding: 32px 28px 120px;
      }
      .site-note {
        display: inline-flex;
        align-items: center;
        min-height: 40px;
        padding: 0 14px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.82);
        color: #2563eb;
        font-size: 13px;
        font-weight: 600;
      }
      @media (max-width: 720px) {
        .shell { padding: 16px; }
        .site { padding: 24px 20px 120px; }
      }
    </style>
  </head>
  <body>
    <div class="shell">
      <div class="meta">
        <div class="chip">${this.escapeHtml(chipServiceNumber)}: ${this.escapeHtml(snId || '-')}</div>
        <div class="chip">${this.escapeHtml(chipPosition)}: ${this.escapeHtml(position)}</div>
        <div class="chip">${this.escapeHtml(chipButton)}: ${this.escapeHtml(buttonSize)}</div>
        <div class="chip">${this.escapeHtml(chipAnimation)}: ${this.escapeHtml(animation)}</div>
      </div>
      <section class="panel">
        <div class="site">
          <div class="site-note">${this.escapeHtml(siteNote)}</div>
        </div>
      </section>
    </div>
    <script>
      window.AileWebChatConfig = Object.assign(${safeConfigJson}, {
        onLoad: function () { console.log('AileWebChat loaded'); },
        onError: function (err) { console.error('AileWebChat failed', err); }
      });
    </script>
    <script src="${this.escapeHtml(loaderUrl)}" async></script>
  </body>
</html>`;
  }

  private isThemeDirty(src: {
    primaryColor: string;
    backgroundColor: string;
    headerBg: string;
    headerTextColor: string;
    footerBg: string;
    customerBubbleBg: string;
    customerBubbleText: string;
    agentBubbleBg: string;
    agentBubbleText: string;
    bubbleRadius: string;
    windowRadius: string;
    boxShadowMedium: string;
    fontSizeBase: string;
  }): boolean {
    if (!src) return false;
    const eq = (a: string, b: string) => String(a || '') === String(b || '');
    return !(
      eq(this.primaryColor, src.primaryColor) &&
      eq(this.backgroundColor, src.backgroundColor) &&
      eq(this.headerBg, src.headerBg) &&
      eq(this.headerTextColor, src.headerTextColor) &&
      eq(this.footerBg, src.footerBg) &&
      eq(this.customerBubbleBg, src.customerBubbleBg) &&
      eq(this.customerBubbleText, src.customerBubbleText) &&
      eq(this.agentBubbleBg, src.agentBubbleBg) &&
      eq(this.agentBubbleText, src.agentBubbleText) &&
      eq(this.bubbleRadius, src.bubbleRadius) &&
      eq(this.windowRadius, src.windowRadius) &&
      eq(this.boxShadowMedium, src.boxShadowMedium) &&
      eq(this.fontSizeBase, src.fontSizeBase)
    );
  }

  hasUnsavedChanges(): boolean {
    const hasFloatDirty = this.loadedFloatWindow ? this.isFloatWindowDirty(this.loadedFloatWindow) : false;
    const hasThemeDirty = this.loadedThemeCfg ? this.isThemeDirty(this.loadedThemeCfg) : false;
    const hasFeatureDirty = this.isFeatureConfigDirty();
    return hasFloatDirty || hasThemeDirty || hasFeatureDirty || !!this.headerImageFile;
  }

  copyIntegrationCode(): void {
    const text = this.integrationCodeSnippet || '';
    if ((navigator as any).clipboard && (navigator as any).clipboard.writeText) {
      (navigator as any).clipboard
        .writeText(text)
        .then(() => {
          this.msg.success(this.i18n.fanyi('servicenumber.webchat.integration.copySuccess'));
        })
        .catch(() => {
          this.copyByTextarea(text);
        });
      return;
    }
    this.copyByTextarea(text);
  }

  private copyByTextarea(text: string): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try {
      navigator.clipboard
        .writeText(ta.value)
        .then(() => {
          this.msg.success(this.i18n.fanyi('servicenumber.webchat.integration.copySuccess'));
        })
        .catch(() => {
          this.msg.error(this.i18n.fanyi('servicenumber.webchat.integration.copyFailed'));
        });
      this.msg.success(this.i18n.fanyi('servicenumber.webchat.integration.copySuccess'));
    } catch (err) {
      this.msg.error(this.i18n.fanyi('servicenumber.webchat.integration.copyFailed'));
    } finally {
      document.body.removeChild(ta);
    }
  }

  private copyShareUrlByTextarea(text: string): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      this.msg.success(this.i18n.fanyi('servicenumber.webchat.share.copySuccess'));
    } catch {
      this.msg.error(this.i18n.fanyi('servicenumber.webchat.share.copyFailed'));
    } finally {
      document.body.removeChild(ta);
    }
  }

  private updateShareQrCode(): void {
    const link = this.currentWebchatShareUrl;
    if (!link) {
      this.shareQrCodeDataUrl = '';
      return;
    }
    this.shareQrGenerating = true;
    QRCode.toDataURL(link, { width: 256, margin: 1 })
      .then((url: string) => {
        this.shareQrCodeDataUrl = url;
        this.shareQrGenerating = false;
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.shareQrGenerating = false;
        this.shareQrCodeDataUrl = '';
        this.msg.error(this.i18n.fanyi('servicenumber.webchat.share.qrGenerateFailed'));
        this.cdr.detectChanges();
      });
  }

  private createDefaultFeatureConfig(): WebchatFeatureConfig {
    return {
      hideInputWhenAiServing: false,
      showVoiceButton: true,
      showMoreMenu: true,
      showAttachmentButton: true,
      showCloseButton: true
    };
  }

  private cloneFeatureConfig(config: WebchatFeatureConfig): WebchatFeatureConfig {
    return { ...config };
  }

  private createDefaultFloatWindowConfig(): WebchatFloatWindowConfig {
    return { ...DEFAULT_FLOAT_WINDOW_CONFIG };
  }

  private normalizeFloatWindowConfig(raw: unknown): WebchatFloatWindowConfig {
    const defaults = this.createDefaultFloatWindowConfig();
    const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const position = source['position'] === 'left-bottom' || source['position'] === 'right-bottom' ? source['position'] : defaults.position;
    const buttonSize =
      source['buttonSize'] === 'small' || source['buttonSize'] === 'medium' || source['buttonSize'] === 'large'
        ? source['buttonSize']
        : source['size'] === 'small' || source['size'] === 'medium' || source['size'] === 'large'
          ? source['size']
          : defaults.buttonSize;
    const animation =
      source['animation'] === 'none' ||
      source['animation'] === 'pulse' ||
      source['animation'] === 'bounce' ||
      source['animation'] === 'shake'
        ? source['animation']
        : defaults.animation;
    const badgeColorSource = source['badgeColor'] ?? source['themeColor'];
    const badgeColor = badgeColorSource == null || String(badgeColorSource).trim() === '' ? defaults.badgeColor : String(badgeColorSource);
    const rawAvatarUrl = this.readRawFloatWindowAvatarUrl(source);

    return {
      position,
      buttonSize,
      badgeColor,
      animation,
      bottomOffset: typeof source['bottomOffset'] === 'number' ? Number(source['bottomOffset']) : defaults.bottomOffset,
      rightOffset: typeof source['rightOffset'] === 'number' ? Number(source['rightOffset']) : defaults.rightOffset,
      leftOffset: typeof source['leftOffset'] === 'number' ? Number(source['leftOffset']) : defaults.leftOffset,
      iconUrl: this.normalizeFloatWindowIconUrl(source) ?? (this.isHttpUrl(rawAvatarUrl) ? rawAvatarUrl : null)
    };
  }

  private normalizeFloatWindowIconUrl(raw: unknown): string | null {
    const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const headerImage = source['headerImage'];
    let headerUrlRaw =
      headerImage && typeof headerImage === 'object' ? String(((headerImage as Record<string, unknown>)['url'] ?? '') || '') : '';
    headerUrlRaw = headerUrlRaw.trim().replace(/^`|`$/g, '');
    if (this.isHttpUrl(headerUrlRaw)) {
      return headerUrlRaw;
    }
    const avatarUrlRaw = this.readRawFloatWindowAvatarUrl(source);
    return this.isHttpUrl(avatarUrlRaw) ? avatarUrlRaw : null;
  }

  private readRawFloatWindowAvatarUrl(raw: unknown): string {
    if (!raw || typeof raw !== 'object') {
      return '';
    }
    const avatarUrlRaw = (raw as Record<string, unknown>)['avatarUrl'];
    return typeof avatarUrlRaw === 'string' ? avatarUrlRaw.trim() : '';
  }

  private createDefaultServiceTermsConfig(): WebchatServiceTermsConfig {
    return {
      enabled: false,
      title: this.i18n.fanyi('servicenumber.webchat.feature.terms.defaultTitle'),
      version: 'v1',
      content: this.i18n.fanyi('servicenumber.webchat.feature.terms.previewPlaceholder'),
      agreeButtonText: this.i18n.fanyi('servicenumber.webchat.feature.terms.defaultAgree'),
      cancelButtonText: ''
    };
  }

  private cloneServiceTermsConfig(config: WebchatServiceTermsConfig): WebchatServiceTermsConfig {
    return { ...config };
  }

  private createDefaultAboutConfig(): WebchatAboutConfig {
    return {
      title: this.i18n.fanyi('servicenumber.webchat.feature.about.defaultTitle'),
      versionDescription: this.i18n.fanyi('servicenumber.webchat.feature.about.defaultVersionDescription')
    };
  }

  private cloneAboutConfig(config: WebchatAboutConfig): WebchatAboutConfig {
    return { ...config };
  }

  private createDefaultPostMessageConfig(): WebchatPostMessageConfig {
    return {
      enabled: false
    };
  }

  private clonePostMessageConfig(config: WebchatPostMessageConfig): WebchatPostMessageConfig {
    return { ...config };
  }

  private readFeatureConfig(raw: unknown): {
    config: WebchatFeatureConfig;
    customized: boolean;
    serviceTermsConfig: WebchatServiceTermsConfig;
    aboutConfig: WebchatAboutConfig;
    aboutFallbackState: WebchatAboutConfigFallbackState;
    postMessageConfig: WebchatPostMessageConfig;
  } {
    if (raw == null || String(raw).trim() === '') {
      return {
        config: this.createDefaultFeatureConfig(),
        customized: false,
        serviceTermsConfig: this.createDefaultServiceTermsConfig(),
        aboutConfig: this.createDefaultAboutConfig(),
        aboutFallbackState: {
          titleUsesDefaultFallback: true,
          versionDescriptionUsesDefaultFallback: true
        },
        postMessageConfig: this.createDefaultPostMessageConfig()
      };
    }

    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return {
        config: this.normalizeFeatureConfig(parsed),
        customized: this.hasManagedFeatureConfig(parsed),
        serviceTermsConfig: this.normalizeServiceTermsConfig(parsed),
        ...this.normalizeAboutConfig(parsed),
        postMessageConfig: this.normalizePostMessageConfig(parsed)
      };
    } catch {
      return {
        config: this.createDefaultFeatureConfig(),
        customized: false,
        serviceTermsConfig: this.createDefaultServiceTermsConfig(),
        aboutConfig: this.createDefaultAboutConfig(),
        aboutFallbackState: {
          titleUsesDefaultFallback: true,
          versionDescriptionUsesDefaultFallback: true
        },
        postMessageConfig: this.createDefaultPostMessageConfig()
      };
    }
  }

  private normalizeFeatureConfig(raw: unknown): WebchatFeatureConfig {
    const defaults = this.createDefaultFeatureConfig();
    const source = raw && typeof raw === 'object' ? (raw as Partial<Record<WebchatFeatureConfigKey, unknown>>) : {};

    return {
      hideInputWhenAiServing: this.toBoolean(source.hideInputWhenAiServing, defaults.hideInputWhenAiServing),
      showVoiceButton: this.toBoolean(source.showVoiceButton, defaults.showVoiceButton),
      showMoreMenu: this.toBoolean(source.showMoreMenu, defaults.showMoreMenu),
      showAttachmentButton: this.toBoolean(source.showAttachmentButton, defaults.showAttachmentButton),
      showCloseButton: this.toBoolean(source.showCloseButton, defaults.showCloseButton)
    };
  }

  private hasManagedFeatureConfig(raw: unknown): boolean {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return false;
    }

    const source = raw as Record<string, unknown>;
    return (
      FEATURE_TOGGLE_KEYS.some(key => Object.prototype.hasOwnProperty.call(source, key)) ||
      ['serviceTermsConfig', 'aboutConfig', 'postMessageConfig'].some(key => Object.prototype.hasOwnProperty.call(source, key))
    );
  }

  private normalizeServiceTermsConfig(raw: unknown): WebchatServiceTermsConfig {
    if (!raw || typeof raw !== 'object') {
      return this.createDefaultServiceTermsConfig();
    }
    const source = (raw as Record<string, unknown>)['serviceTermsConfig'];
    if (!source || typeof source !== 'object') {
      return this.createDefaultServiceTermsConfig();
    }
    const data = source as Record<string, unknown>;
    const defaults = this.createDefaultServiceTermsConfig();
    return {
      enabled: this.toBoolean(data['enabled'], defaults.enabled),
      title: typeof data['title'] === 'string' ? data['title'] : defaults.title,
      version: typeof data['version'] === 'string' ? data['version'] : defaults.version,
      content: typeof data['content'] === 'string' ? data['content'] : defaults.content,
      agreeButtonText: typeof data['agreeButtonText'] === 'string' ? data['agreeButtonText'] : defaults.agreeButtonText,
      cancelButtonText: this.normalizeServiceTermsCancelButtonText(data['cancelButtonText'], defaults.cancelButtonText)
    };
  }

  private normalizeServiceTermsCancelButtonText(value: unknown, fallback: string): string {
    if (typeof value !== 'string') {
      return fallback;
    }

    const normalized = value.trim();
    return normalized === 'servicenumber.webchat.feature.terms.defaultCancel' ? '' : value;
  }

  private normalizeAboutConfig(raw: unknown): {
    aboutConfig: WebchatAboutConfig;
    aboutFallbackState: WebchatAboutConfigFallbackState;
  } {
    const defaults = this.createDefaultAboutConfig();
    if (!raw || typeof raw !== 'object') {
      return {
        aboutConfig: defaults,
        aboutFallbackState: {
          titleUsesDefaultFallback: true,
          versionDescriptionUsesDefaultFallback: true
        }
      };
    }
    const source = (raw as Record<string, unknown>)['aboutConfig'];
    if (!source || typeof source !== 'object') {
      return {
        aboutConfig: defaults,
        aboutFallbackState: {
          titleUsesDefaultFallback: true,
          versionDescriptionUsesDefaultFallback: true
        }
      };
    }
    const data = source as Record<string, unknown>;
    return {
      aboutConfig: {
        title: typeof data['title'] === 'string' ? data['title'] : defaults.title,
        versionDescription: typeof data['versionDescription'] === 'string' ? data['versionDescription'] : defaults.versionDescription
      },
      aboutFallbackState: {
        titleUsesDefaultFallback: typeof data['title'] !== 'string',
        versionDescriptionUsesDefaultFallback: typeof data['versionDescription'] !== 'string'
      }
    };
  }

  private normalizePostMessageConfig(raw: unknown): WebchatPostMessageConfig {
    if (!raw || typeof raw !== 'object') {
      return this.createDefaultPostMessageConfig();
    }
    const source = (raw as Record<string, unknown>)['postMessageConfig'];
    if (!source || typeof source !== 'object') {
      return this.createDefaultPostMessageConfig();
    }
    const data = source as Record<string, unknown>;
    const defaults = this.createDefaultPostMessageConfig();
    return {
      enabled: this.toBoolean(data['enabled'], defaults.enabled)
    };
  }

  private toBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private buildFeatureConfigPayload(): WebchatFeatureConfig & {
    serviceTermsConfig: WebchatServiceTermsConfig;
    aboutConfig: WebchatAboutConfig;
    postMessageConfig: WebchatPostMessageConfig;
  } {
    return {
      ...this.featureConfig,
      serviceTermsConfig: this.serviceTermsConfig,
      aboutConfig: this.aboutConfig,
      postMessageConfig: this.postMessageConfig
    };
  }

  private computeFeatureConfigCustomized(): boolean {
    return (
      this.isToggleFeatureConfigDirty() ||
      this.isServiceTermsConfigDirtyFromDefault() ||
      this.isAboutConfigDirtyFromDefault() ||
      this.postMessageConfig.enabled !== this.createDefaultPostMessageConfig().enabled
    );
  }

  private isToggleFeatureConfigDirty(): boolean {
    return FEATURE_TOGGLE_KEYS.some(key => this.featureConfig[key] !== this.createDefaultFeatureConfig()[key]);
  }

  private isServiceTermsConfigDirtyFromDefault(): boolean {
    const defaults = this.createDefaultServiceTermsConfig();
    return (
      this.serviceTermsConfig.enabled !== defaults.enabled ||
      this.serviceTermsConfig.title !== defaults.title ||
      this.serviceTermsConfig.version !== defaults.version ||
      this.serviceTermsConfig.content !== defaults.content ||
      this.serviceTermsConfig.agreeButtonText !== defaults.agreeButtonText ||
      this.serviceTermsConfig.cancelButtonText !== defaults.cancelButtonText
    );
  }

  private isAboutConfigDirtyFromDefault(): boolean {
    const defaults = this.createDefaultAboutConfig();
    return this.aboutConfig.title !== defaults.title || this.aboutConfig.versionDescription !== defaults.versionDescription;
  }

  private isFeatureConfigDirty(): boolean {
    if (this.featureConfigCustomized !== this.loadedFeatureConfigCustomized) {
      return true;
    }

    return (
      FEATURE_TOGGLE_KEYS.some(key => this.featureConfig[key] !== this.loadedFeatureConfig[key]) ||
      this.serviceTermsConfig.enabled !== this.loadedServiceTermsConfig.enabled ||
      this.serviceTermsConfig.title !== this.loadedServiceTermsConfig.title ||
      this.serviceTermsConfig.version !== this.loadedServiceTermsConfig.version ||
      this.serviceTermsConfig.content !== this.loadedServiceTermsConfig.content ||
      this.serviceTermsConfig.agreeButtonText !== this.loadedServiceTermsConfig.agreeButtonText ||
      this.serviceTermsConfig.cancelButtonText !== this.loadedServiceTermsConfig.cancelButtonText ||
      this.aboutConfig.title !== this.loadedAboutConfig.title ||
      this.aboutConfig.versionDescription !== this.loadedAboutConfig.versionDescription ||
      this.postMessageConfig.enabled !== this.loadedPostMessageConfig.enabled
    );
  }
}
